/**
 * The home-screen widgets' snapshot, version 1 (`D280`).
 *
 * The shell's widgets (`SnapshotWidget.java`) draw what this builds and
 * nothing else: no request, no locale, no arithmetic of their own. So every
 * string here is final — in the app's language, already formatted — and a
 * card that has nothing honest to say is ABSENT, which the widget draws as
 * "open the app to see it here".
 *
 * ── THE CONTRACT ───────────────────────────────────────────────────────────
 *
 *     { v: 1, day: "YYYY-MM-DD",
 *       today?, week?, recovery?: { eyebrow, headline, lines: string[] } }
 *
 * `day` is the device's LOCAL date the snapshot was written for; the shell
 * draws `today` only on that date. At most three `lines` are drawn. A field
 * added inside version 1 is ignored by an older shell; anything an older
 * shell would misread raises `v` — and `versionCode` with it (`D166`),
 * because a shell renders only the version it knows.
 */
import { weekToGo } from "@repo/client/session";
import type { RecoveryOutput, TrainingStats } from "@repo/contract/training";
import {
  "native-release.widget_recovery_all_ready" as recoveryAllReady,
  "native-release.widget_recovery_eyebrow" as recoveryEyebrow,
  "native-release.widget_recovery_muscle" as recoveryMuscle,
  "native-release.widget_recovery_tired" as recoveryTired,
  "native-release.widget_today_dose" as todayDose,
  "native-release.widget_today_eyebrow" as todayEyebrow,
  "native-release.widget_today_live" as todayLive,
  "native-release.widget_today_no_plan" as todayNoPlan,
  "native-release.widget_today_rest" as todayRest,
  "native-release.widget_week_closed" as weekClosed,
  "native-release.widget_week_count" as weekCount,
  "native-release.widget_week_eyebrow" as weekEyebrow,
  "native-release.widget_week_streak" as weekStreak,
  "native-release.widget_week_to_go" as weekToGoText,
} from "@repo/i18n";

export const WIDGET_SNAPSHOT_VERSION = 1;

/** How many muscles the readiness widget names. */
const NAMED_MUSCLES = 3;
/** Below this a muscle counts as still tired — Progress's own threshold. */
const TIRED_BELOW = 0.9;

export interface WidgetCard {
  readonly eyebrow: string;
  readonly headline: string;
  readonly lines: readonly string[];
}

export interface WidgetSnapshot {
  readonly day: string;
  readonly recovery?: WidgetCard;
  readonly today?: WidgetCard;
  readonly v: typeof WIDGET_SNAPSHOT_VERSION;
  readonly week?: WidgetCard;
}

/** What Today knows about today's workout, reduced to what a widget says. */
export type TodayFact =
  | { readonly kind: "live"; readonly title: string }
  | { readonly kind: "no-plan" }
  | { readonly kind: "rest" }
  | {
      readonly exercises: number;
      readonly kind: "planned";
      readonly sets: number;
      readonly title: string;
    };

export interface WidgetSources {
  readonly now: Date;
  /** `GET /me/recovery`; absent while unknown. */
  readonly recovery?: RecoveryOutput;
  /** `GET /me/stats`; absent while unknown. */
  readonly stats?: TrainingStats;
  /** Absent while the plan is loading or failed. */
  readonly today?: TodayFact;
}

/** The device's local date as `YYYY-MM-DD` — what the shell compares. */
export function localDay(now: Date): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function todayCard(fact: TodayFact): WidgetCard {
  switch (fact.kind) {
    case "live":
      return {
        eyebrow: todayEyebrow(),
        headline: fact.title,
        lines: [todayLive()],
      };
    case "planned":
      return {
        eyebrow: todayEyebrow(),
        headline: fact.title,
        lines: [
          todayDose({
            exercises: String(fact.exercises),
            sets: String(fact.sets),
          }),
        ],
      };
    case "rest":
      return { eyebrow: todayEyebrow(), headline: todayRest(), lines: [] };
    default:
      return { eyebrow: todayEyebrow(), headline: todayNoPlan(), lines: [] };
  }
}

function weekCard(stats: TrainingStats): WidgetCard {
  const toGo = weekToGo(stats.thisWeek);
  const lines = [
    toGo.kind === "closed"
      ? weekClosed()
      : weekToGoText({ count: String(toGo.count) }),
  ];
  if (stats.streakWeeks > 0) {
    lines.push(weekStreak({ weeks: String(stats.streakWeeks) }));
  }
  return {
    eyebrow: weekEyebrow(),
    headline: weekCount({
      done: String(stats.thisWeek.done),
      scheduled: String(stats.thisWeek.scheduled),
    }),
    lines,
  };
}

function recoveryCard(recovery: RecoveryOutput): WidgetCard {
  const tired = recovery.muscles
    .filter((m) => m.readiness < TIRED_BELOW)
    .sort((a, b) => a.readiness - b.readiness)
    .slice(0, NAMED_MUSCLES);
  return {
    eyebrow: recoveryEyebrow(),
    headline: tired.length === 0 ? recoveryAllReady() : recoveryTired(),
    lines: tired.map((m) =>
      recoveryMuscle({
        name: m.name,
        pct: String(Math.round(m.readiness * 100)),
      })
    ),
  };
}

export function buildWidgetSnapshot(sources: WidgetSources): WidgetSnapshot {
  return {
    day: localDay(sources.now),
    ...(sources.today === undefined ? {} : { today: todayCard(sources.today) }),
    ...(sources.stats === undefined ? {} : { week: weekCard(sources.stats) }),
    ...(sources.recovery === undefined
      ? {}
      : { recovery: recoveryCard(sources.recovery) }),
    v: WIDGET_SNAPSHOT_VERSION,
  };
}
